import '@/polyfills/requestAnimationFrame';
import { ApolloProvider } from '@apollo/client/react';
import * as Sentry from '@sentry/react-native';
import * as Linking from 'expo-linking';
import { router, Stack, usePathname, type Href } from 'expo-router';
import Constants from 'expo-constants';
import * as SplashScreen from 'expo-splash-screen';
import { useCallback, useEffect, useState } from 'react';
import { BottomSheetModalProvider } from '@gorhom/bottom-sheet';
import { GestureHandlerRootView } from 'react-native-gesture-handler';
import { Platform } from 'react-native';
import { SafeAreaProvider, initialWindowMetrics } from 'react-native-safe-area-context';
import { PaperProvider } from 'react-native-paper';
import { en, ja, registerTranslation } from 'react-native-paper-dates';

import { apolloClient } from '@/adapters/graphql/apolloClient';
import { retryOnTransientError } from '@/adapters/graphql/retryOnTransientError';
import { isTransientError, TRANSIENT_ERROR_BREADCRUMB } from '@/adapters/graphql/transientError';
import { APP_FLAVOR } from '@/adapters/config/env';
import { appTheme, unisonColors } from '@/theme';
import { setCurrentAccountId } from '@/adapters/analytics/currentAccountId';
import { setCurrentUserId } from '@/adapters/analytics/currentUserId';
import { mobileReplayOptions } from '@/adapters/logger/mobileReplayOptions';
import { sentryLogger } from '@/adapters/logger/sentryLogger';
import { migrateKeychainAccessibility } from '@/adapters/storage/migrateKeychainAccessibility';
import { beforeBreadcrumb, scrubEvent } from '@/adapters/logger/sanitizer';
import { registerBackgroundHandler } from '@/adapters/pushNotification/backgroundHandler';
import { registerForegroundHandler } from '@/adapters/pushNotification/foregroundHandler';
import { useFirebaseWebInitialization } from '@/adapters/pushNotification/useFirebaseWebInitialization';
import {
  initializeNotificationChannel,
  initializeNotificationHandler,
} from '@/adapters/pushNotification/notificationDisplay';
import { sendReadReceipt } from '@/adapters/pushNotification/deliveryReceipt';
import { getNavigationTargetFromResponse } from '@/adapters/pushNotification/notificationTapHandler';
import { useNotificationTapResponse } from '@/adapters/pushNotification/useNotificationTapResponse';
import { StartupErrorState } from '@/components/atoms/state';
import { SnackbarProvider } from '@/components/organisms/global-alert/SnackbarProvider';
import { NotificationPermissionPrompt } from '@/components/organisms/notifications/NotificationPermissionPrompt';
import { MeDocument } from '@/generated/usermobile-bff/graphql';
import {
  useAuthStore,
  useDeviceRegistrationStore,
  useNotificationPromptStore,
  useNotificationAnalyticsStore,
  useScreenAnalyticsStore,
  useRemoteConfigStore,
} from '@/stores';
import { OnboardingStep } from '@/stores/createOnboardingStore';
import { useAiTranscriptionConsentStore } from '@/stores/useAiTranscriptionConsentStore';
import { useOnboardingStore } from '@/stores/useOnboardingStore';
import { useSettingsStore } from '@/stores/useSettingsStore';

const sentryDsn = process.env.EXPO_PUBLIC_SENTRY_DSN;

registerTranslation('en', en);
registerTranslation('ja', ja);

if (sentryDsn) {
  Sentry.init({
    dsn: sentryDsn,
    environment: APP_FLAVOR,
    debug: false,
    tracesSampleRate: __DEV__ ? 1.0 : 0.5,
    profilesSampleRate: __DEV__ ? 1.0 : 0.5,
    replaysSessionSampleRate: __DEV__ ? undefined : 0.1,
    replaysOnErrorSampleRate: __DEV__ ? undefined : 1.0,
    beforeSend: scrubEvent,
    beforeBreadcrumb,
    integrations: (defaults) =>
      __DEV__
        ? defaults.filter((integration) => integration.name !== 'TurboModuleContext')
        : [...defaults, Sentry.mobileReplayIntegration(mobileReplayOptions)],
  });
}

registerBackgroundHandler();
initializeNotificationHandler();
void initializeNotificationChannel();

SplashScreen.preventAutoHideAsync();

const gestureRootStyle = { flex: 1 } as const;

const RootLayout = () => {
  const loadStoredAuth = useAuthStore((state) => state.loadStoredAuth);
  const loadOnboarding = useOnboardingStore((state) => state.loadOnboarding);
  const loadSettings = useSettingsStore((state) => state.loadSettings);
  const loadAiTranscriptionConsent = useAiTranscriptionConsentStore((state) => state.loadConsent);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const requestPermissionAndRegister = useDeviceRegistrationStore(
    (state) => state.requestPermissionAndRegister,
  );
  const unregisterDevice = useDeviceRegistrationStore((state) => state.unregister);
  const getDeviceRegistrationState = useDeviceRegistrationStore.getState;
  const onboardingUserId = useOnboardingStore((state) => state.userId);
  const isOnboardingCompleted = useOnboardingStore((state) => state.isCompleted);
  const isOnboardingSkipped = useOnboardingStore((state) => state.isSkipped);
  const evaluateNotificationPrompt = useNotificationPromptStore((state) => state.evaluate);
  const skipNotificationPrompt = useNotificationPromptStore((state) => state.skip);
  const dismissNotificationPrompt = useNotificationPromptStore((state) => state.dismiss);
  const isNotificationPromptVisible = useNotificationPromptStore((state) => state.isVisible);
  const notificationPermissionStatus = useNotificationPromptStore(
    (state) => state.permissionStatus,
  );

  const [isReady, setIsReady] = useState(false);
  const [hasStartupError, setStartupError] = useState(false);
  const [isRetryingStartup, setIsRetryingStartup] = useState(false);

  useFirebaseWebInitialization();

  const prepare = useCallback(async () => {
    setStartupError(false);

    try {
      await migrateKeychainAccessibility();
      await Promise.all([
        loadStoredAuth(),
        loadSettings(),
        loadAiTranscriptionConsent(),
        useRemoteConfigStore.getState().initialize(),
      ]);

      const isAuthenticatedNow = useAuthStore.getState().isAuthenticated;

      if (!isAuthenticatedNow) {
        useOnboardingStore.setState({
          currentStep: OnboardingStep.welcome,
          isCompleted: false,
          isLoading: false,
          userId: null,
        });

        return;
      }

      try {
        const { data } = await retryOnTransientError(() =>
          apolloClient.query({ query: MeDocument }),
        );

        if (!data?.me) {
          throw new Error('Me query returned no data');
        }

        setCurrentUserId(data.me.id);
        setCurrentAccountId(data.me.accountId);
        await loadOnboarding(data.me.id);
      } catch (error) {
        if (isTransientError(error)) {
          sentryLogger.addBreadcrumb(TRANSIENT_ERROR_BREADCRUMB, { operationName: 'Me' });
          setStartupError(true);

          return;
        }

        sentryLogger.captureException(error);
        await useAuthStore.getState().logout();
        setCurrentUserId(null);
        setCurrentAccountId(null);
        useOnboardingStore.setState({
          currentStep: OnboardingStep.welcome,
          isCompleted: false,
          isLoading: false,
          userId: null,
        });
      }
    } finally {
      setIsReady(true);
    }
  }, [loadAiTranscriptionConsent, loadOnboarding, loadStoredAuth, loadSettings]);

  useEffect(() => {
    void prepare();
  }, [prepare]);

  const handleRetryStartup = useCallback(() => {
    setIsRetryingStartup(true);
    void prepare().finally(() => setIsRetryingStartup(false));
  }, [prepare]);

  useEffect(() => {
    if (!isReady) {
      return;
    }

    SplashScreen.hideAsync();
  }, [isReady]);

  useEffect(() => {
    if (Platform.OS !== 'web') {
      return;
    }

    document.title = Constants.expoConfig?.name ?? 'Digima';
  }, []);

  useEffect(() => {
    if (!isReady || isAuthenticated) {
      return;
    }

    router.replace('/');
  }, [isReady, isAuthenticated]);

  const lastNotificationResponse = useNotificationTapResponse();

  const trackPushNotificationReceived = useNotificationAnalyticsStore(
    (state) => state.trackPushNotificationReceived,
  );
  const trackPushNotificationOpened = useNotificationAnalyticsStore(
    (state) => state.trackPushNotificationOpened,
  );
  const trackDeepLinkHandled = useNotificationAnalyticsStore((state) => state.trackDeepLinkHandled);
  const trackNotificationPermissionPromptEnabled = useNotificationAnalyticsStore(
    (state) => state.trackNotificationPermissionPromptEnabled,
  );
  const trackNotificationPermissionPromptSkipped = useNotificationAnalyticsStore(
    (state) => state.trackNotificationPermissionPromptSkipped,
  );

  useEffect(() => useRemoteConfigStore.getState().subscribeToUpdates(), []);

  useEffect(() => {
    const unsubscribe = registerForegroundHandler({
      onMessageReceived: trackPushNotificationReceived,
    });

    return unsubscribe;
  }, [trackPushNotificationReceived]);

  useEffect(() => {
    // The Stack is not mounted while the startup error screen is showing, so a
    // push here would be dropped rather than deferred.
    if (!isReady || !isAuthenticated || hasStartupError) {
      return;
    }

    const navigationTarget = getNavigationTargetFromResponse(lastNotificationResponse);

    if (!navigationTarget) {
      return;
    }

    trackPushNotificationOpened(navigationTarget.resourceType);

    if (navigationTarget.userDispatchId) {
      void sendReadReceipt(navigationTarget.userDispatchId);
    }

    const route: Href | null =
      navigationTarget.resourceType === 'contact'
        ? (`/contact/${navigationTarget.resourceId}` as Href)
        : navigationTarget.resourceType === 'meeting'
          ? (`/meeting/${navigationTarget.resourceId}` as Href)
          : navigationTarget.resourceType === 'agent_handoff'
            ? (`/handoff/${navigationTarget.resourceId}` as Href)
            : null;

    if (!route) {
      sentryLogger.addBreadcrumb('Notification tap ignored because resource type is unsupported', {
        resourceType: navigationTarget.resourceType,
        resourceId: navigationTarget.resourceId,
      });

      return;
    }

    trackDeepLinkHandled(navigationTarget.resourceType);

    const frameId =
      typeof requestAnimationFrame !== 'undefined'
        ? requestAnimationFrame(() => {
            router.push(route);
          })
        : null;

    return () => {
      if (frameId !== null) {
        cancelAnimationFrame(frameId);
      }
    };
  }, [
    isReady,
    isAuthenticated,
    hasStartupError,
    lastNotificationResponse,
    trackPushNotificationOpened,
    trackDeepLinkHandled,
  ]);

  useEffect(() => {
    // prepare() owns the startup Me fetch; re-running it here while it is in an
    // error state would re-report the failure it deliberately suppressed.
    if (!isReady || !isAuthenticated || hasStartupError) return;

    const { userId } = useOnboardingStore.getState();
    if (userId) return;

    const loadOnboardingForUser = async () => {
      try {
        const { data } = await retryOnTransientError(() =>
          apolloClient.query({ query: MeDocument }),
        );
        if (data?.me) {
          setCurrentUserId(data.me.id);
          setCurrentAccountId(data.me.accountId);
          await loadOnboarding(data.me.id);
        }
      } catch (error) {
        if (isTransientError(error)) {
          sentryLogger.addBreadcrumb(TRANSIENT_ERROR_BREADCRUMB, { operationName: 'Me' });

          return;
        }

        sentryLogger.captureException(error);
      }
    };

    void loadOnboardingForUser();
  }, [isReady, isAuthenticated, hasStartupError, loadOnboarding]);

  useEffect(() => {
    if (!isAuthenticated) {
      if (getDeviceRegistrationState().isRegistered) {
        void unregisterDevice();
      }

      return;
    }

    if (!onboardingUserId || (!isOnboardingCompleted && !isOnboardingSkipped)) {
      return;
    }

    void evaluateNotificationPrompt(onboardingUserId);
  }, [
    isAuthenticated,
    onboardingUserId,
    isOnboardingCompleted,
    isOnboardingSkipped,
    evaluateNotificationPrompt,
    unregisterDevice,
    getDeviceRegistrationState,
  ]);

  useEffect(() => {
    if (notificationPermissionStatus === 'granted') {
      void requestPermissionAndRegister();
    }
  }, [notificationPermissionStatus, requestPermissionAndRegister]);

  const handleEnableNotifications = () => {
    trackNotificationPermissionPromptEnabled();
    dismissNotificationPrompt();

    if (notificationPermissionStatus === 'denied') {
      void Linking.openSettings();

      return;
    }

    void requestPermissionAndRegister();
  };

  const handleSkipNotificationPrompt = () => {
    trackNotificationPermissionPromptSkipped();

    if (onboardingUserId) {
      void skipNotificationPrompt(onboardingUserId);
    }
  };

  const trackScreenView = useScreenAnalyticsStore((state) => state.trackScreenView);
  const pathname = usePathname();

  useEffect(() => {
    if (!pathname) {
      return;
    }

    trackScreenView(pathname);
  }, [pathname, trackScreenView]);

  return (
    <SafeAreaProvider initialMetrics={initialWindowMetrics}>
      <GestureHandlerRootView style={gestureRootStyle}>
        <ApolloProvider client={apolloClient}>
          <PaperProvider theme={appTheme}>
            {/* Innermost: bottom sheets are portalled here, so they must sit inside the
                Apollo and Paper providers to inherit the client and the app theme. */}
            <BottomSheetModalProvider>
              {hasStartupError ? (
                <StartupErrorState
                  onRetry={handleRetryStartup}
                  isRetrying={isRetryingStartup}
                  testID="startup-error"
                />
              ) : (
                <>
                  <Stack
                    screenOptions={{
                      headerShown: false,
                      headerTransparent: true,
                      headerShadowVisible: false,
                      headerTintColor: unisonColors.textNdPrimary,
                      contentStyle: { backgroundColor: appTheme.colors.background },
                    }}>
                    <Stack.Screen name="(onboarding)" options={{ headerShown: false }} />
                    <Stack.Screen name="(drawer)" options={{ title: '' }} />
                    <Stack.Screen
                      name="search/contacts"
                      options={{ presentation: 'modal', headerShown: false }}
                    />
                    <Stack.Screen
                      name="contact/[id]"
                      options={{ headerShown: true, title: '', headerBackTitle: '' }}
                    />
                    <Stack.Screen name="meeting/[id]" options={{ headerShown: false }} />
                    <Stack.Screen name="handoff/[id]/index" options={{ headerShown: false }} />
                    <Stack.Screen name="handoff/[id]/sms" options={{ headerShown: false }} />
                    <Stack.Screen
                      name="recording"
                      options={{ headerShown: false, animation: 'fade' }}
                    />
                  </Stack>
                  <SnackbarProvider />
                  {isNotificationPromptVisible ? (
                    <NotificationPermissionPrompt
                      onEnable={handleEnableNotifications}
                      onSkip={handleSkipNotificationPrompt}
                    />
                  ) : null}
                </>
              )}
            </BottomSheetModalProvider>
          </PaperProvider>
        </ApolloProvider>
      </GestureHandlerRootView>
    </SafeAreaProvider>
  );
};

export default sentryDsn ? Sentry.wrap(RootLayout) : RootLayout;
